
import React, { Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";

// Lazy load route components for better initial bundle size
const Index = React.lazy(() => import("./pages/Index"));
const Contact = React.lazy(() => import("./pages/Contact"));
const PrivacyPolicy = React.lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = React.lazy(() => import("./pages/TermsOfService"));
const Domains = React.lazy(() => import("./pages/Domains"));
const DomainDetail = React.lazy(() => import("./pages/DomainDetail"));
const Solutions = React.lazy(() => import("./pages/Solutions"));
const KadenceTalent = React.lazy(() => import("./pages/KadenceTalent"));
const KadenceSearch = React.lazy(() => import("./pages/KadenceSearch"));
const KadencePHD = React.lazy(() => import("./pages/KadencePHD"));
const RedisPartnership = React.lazy(() => import("./pages/RedisPartnership"));
const AIAdvisory = React.lazy(() => import("./pages/AIAdvisory"));
const Community = React.lazy(() => import("./pages/Community"));
const Opportunities = React.lazy(() => import("./pages/Opportunities"));
const CustomerStories = React.lazy(() => import("./pages/CustomerStories"));
const Blogs = React.lazy(() => import("./pages/Blogs"));
const CaseStudies = React.lazy(() => import("./pages/CaseStudies"));
const CaseStudyDetail = React.lazy(() => import("./pages/CaseStudyDetail"));
const BlogArticle = React.lazy(() => import("./pages/BlogArticle"));
const NotFound = React.lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-kadence-darknavy">
    <div className="animate-pulse flex flex-col items-center">
      <div className="h-8 w-8 border-2 border-kadence-pink border-t-transparent rounded-full animate-spin mb-4"></div>
      <span className="text-gray-400 text-sm">Loading...</span>
    </div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        {/* Skip to main content link for accessibility */}
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-kadence-pink focus:text-kadence-darknavy focus:rounded-md focus:font-medium"
        >
          Skip to main content
        </a>
        <div className="flex flex-col min-h-screen">
          <Navbar />
          <main id="main-content" className="flex-grow">
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/domains" element={<Domains />} />
                <Route path="/domains/:slug" element={<DomainDetail />} />
                <Route path="/solutions" element={<Solutions />} />
                <Route path="/kadence-talent" element={<KadenceTalent />} />
                <Route path="/kadence-search" element={<KadenceSearch />} />
                <Route path="/kadence-phd" element={<KadencePHD />} />
                <Route path="/redis-partnership" element={<RedisPartnership />} />
                <Route path="/ai-advisory" element={<AIAdvisory />} />
                <Route path="/community" element={<Community />} />
                <Route path="/opportunities" element={<Opportunities />} />
                <Route path="/testimonials" element={<CustomerStories />} />
                <Route path="/customer-stories" element={<CustomerStories />} />
                <Route path="/case-studies" element={<CaseStudies />} />
                <Route path="/case-studies/:slug" element={<CaseStudyDetail />} />
                <Route path="/blogs" element={<Blogs />} />
                <Route path="/blogs/:slug" element={<BlogArticle />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </main>
          <Footer />
        </div>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
